import PaginationControls from "./PaginationControls";
import ListStatsControls from "./ListStatsControls";
import Throttler from "../../../Utils/js/Throttler";
import StationListItem from "./StationListItem";

const widthElementSelectors = [
    ".station-parameter__title",
    ".station-parameter__value",
    ".station-parameter__date"
];

class StationListView {
    constructor(options) {
        this._stationDetailComponent = options.stationDetailComponent;
        this.stationDetailComponentOptions = options.stationDetailComponentOptions;
        this.$scrollingContainer = options.$scrollingContainer;
        this.$container = this.$scrollingContainer.find(".station-list");
        this.$listView = this.$container.find(".station-list__container");
        window.$ = this.$ = options.$;         
        
        this.stations = {};
        this.stationIds = []; // Used for list sequencing/order
        
        this._currentHighlightedStationId = null;
        
        this._onListItemMouseenterListeners = [];
        this._onListItemMouseleaveListeners = [];
        
        this.updateWidthsThrottler = new Throttler(500);
        this.firstUpdateWidths = true;
        this.updateWidths = this.updateWidths.bind(this);
        
        this.$listView.on("mouseenter",
        ".station-list__item",
        ev => {
            ev.preventDefault();
            
            this.invokeListItemMouseenterListeners(this.$(ev.currentTarget).data("list-item"), ev);
        })
        
        this.$listView.on("mouseleave",
        ".station-list__item",
        ev => {
            ev.preventDefault();
            
            this.invokeListItemMouseleaveListeners(this.$(ev.currentTarget).data("list-item"), ev);
        })
        
        this.listStats = new ListStatsControls(this.$container.find(".list-stats"));
        
        this.pagination = new PaginationControls( {
            $paginationRoot: this.$container.find(".pagination"),
            pageNumberLabel: options.pageNumberLabel,
            $: this.$
        });
        this.pagination.addPageSelectedListener((page) => {
            this.updateView();
        });
        this.pagination.addPaginationEnabledListener((enabled) => {
            this.updateView();
        });
        
        this.$(window).resize(ev => {
            this.scheduleUpdateWidths();
        });
        
        this.$listView.on("layoutChanged", ev => {
            // Event emitted from StationDetailComponent when its layout changes, usually when you click the "show more" button
            this.updateWidths();
        });
    }
    
    showRange(range) {
        let relevantStationIndex = 0;
        
        for (let i = 0; i < this.stationIds.length; i++) {
            let stationId = this.stationIds[i];
            let station = this.stations[stationId];
            
            if (station.visible && !station.relevant) {
                station.visible = false;
            } else if (station.relevant) {
                // If this station is allowed to be visible ("relevant")
                // Check if its index amongst other "relevant" stations is contained within the page.range
                if (relevantStationIndex >= range[0] && relevantStationIndex <= range[1]) {
                    // If so, make it visible
                    if (!station.visible) {
                        station.visible = true;
                    }
                } else {
                    // Otherwise, it's on another page
                    if (station.visible) {
                        station.visible = false;
                    }
                }
                
                relevantStationIndex++;
            }
        }
    }
    
    scheduleUpdateWidths() {
        if (this.firstUpdateWidths) {
            this.updateWidths();
        } else {
            this.updateWidthsThrottler.debounce(this.updateWidths);
        }
    }
    
    updateWidths() {
        const items = this.$listView.get(0);
        
        let anyUpdated = false;
        
        for (let i = 0; i < widthElementSelectors.length; i++) {
            const selector = widthElementSelectors[i];
            const width = this.updateWidthsOfElements(items.querySelectorAll(selector));
            
            if (width > 0) {
                anyUpdated = true;
            }
        }
        
        if (this.firstUpdateWidths) {
            this.firstUpdateWidths = !anyUpdated;
        }
    }
    
    updateWidthsOfElements(elements) {
        let maxWidthOfElements = 0;
        
        if (elements.length === 0) {
            return 0;
        }
        
        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            element.style.width = "";
        }
        
        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            maxWidthOfElements = Math.max(maxWidthOfElements, element.clientWidth);
        }
        
        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            element.style.width = maxWidthOfElements + "px";
        }
        
        return maxWidthOfElements;
    }
    
    setHighlightedStationId(stationId) {
        if (stationId === null) {
            if (this._currentHighlightedStationId !== null && this._currentHighlightedStationId in this.stations) {
                this.stations[this._currentHighlightedStationId].highlight = false;
            }
            
            this._currentHighlightedStationId = null;
        } else if (this._currentHighlightedStationId !== stationId) {
            if (this._currentHighlightedStationId !== null && this._currentHighlightedStationId in this.stations) {
                this.stations[this._currentHighlightedStationId].highlight = false;
            }
            
            this._currentHighlightedStationId = stationId;
            let station = this.stations[this._currentHighlightedStationId];
            station.highlight = true;
            
            // Scroll the scrollingContainer to the top edge of the station detail $element
            let scrollTop = station.$element.offset().top -
            this.$scrollingContainer.offset().top +
            this.$scrollingContainer.scrollTop();
            
            // Center it in the middle
            scrollTop -= (this.$scrollingContainer.outerHeight() / 2) - (station.$element.outerHeight() / 2);
            
            this.$scrollingContainer.animate({
                scrollTop: scrollTop
            },  8 * 1000 / 60);
        }
    }
    
    addStations(stations) {
        for (let i = 0; i < stations.length; i++) {
            this.addStation(stations[i], true);
        }
        
        this.updateView();
    }
    
    addStation(station, ignoreViewUpdate) {
        let listItem = new StationListItem({
            station: station, 
            stationDetailComponent: this._stationDetailComponent,
            $: this.$,
            stationDetailComponentOptions: this.stationDetailComponentOptions
        });
        let $element = listItem.$element;
        
        this.$listView.append($element);
        
        this.stationIds.push(station.id);
        this.stations[station.id] = listItem;
        
        if (!ignoreViewUpdate) {
            this.updateView();
        }
    }
    
    removeStation(id, ignoreViewUpdate) {
        if (!(id in this.stations)) {
            return;
        }
        
        let listItem = this.stations[id];
        listItem.$element.remove();
        
        this.stationIds = this.stationIds.filter(_id => id !== _id);
        delete this.stations[id];
        
        if (!ignoreViewUpdate) {
            this.updateView();
        }
    }
    
    clearStations() {
        this.$listView.empty();
        this.stations = {};
        
        this.updateView();
    }
    
    get relevantStations() {
        return this.stationIds.map(stationId => this.stations[stationId]).filter(s => s.relevant);
    }
    
    updateStationRelevance(stationId, willBeRelevant, ignoreViewUpdate) {
        this.stations[stationId].relevant = willBeRelevant;
        
        if (!ignoreViewUpdate) {
            this.updateView();
        }
    }
    
    shouldStationBeRelevant(station, mapExtent) {
        let withinMapExtent = true;
        
        if (typeof mapExtent !== typeof {}) {
            mapExtent = null;
        }
        
        if (mapExtent !== null) {
            withinMapExtent = mapExtent.contains(station.point);
        }
        
        return withinMapExtent;
    }
    
    updateAllStationsRelevance(filteredStations, mapExtent) {
        for (let i = 0; i < this.stationIds.length; i++) {
            let stationId = this.stationIds[i];
            this.updateStationRelevance(stationId, false, true);
        }
        
        for (let i = 0; i < filteredStations.length; i++) {
            let station = filteredStations[i];
            
            this.updateStationRelevance(station.id, this.shouldStationBeRelevant(station, mapExtent), true);
        }
        
        this.updateView();
    }
    
    updateView() {
        let relevantStations = this.relevantStations;
        
        this.pagination.totalItems = relevantStations.length;
        this.listStats.total = relevantStations.length;
        
        if (!this.pagination.enabled) {
            this.listStats.numberVisible = 0;
            
            for (let i = 0; i < this.stationIds.length; i++) {
                let station = this.stations[this.stationIds[i]];
                
                station.visible = station.relevant;
            }
        } else {
            const range = this.pagination.range
            this.listStats.numberVisible = range[1] - range[0] + 1;
            this.listStats.start = range[0] + 1;
            
            this.showRange(this.pagination.range);
        }
        
        this.listStats.update();
        this.updateWidths();
    }
    
    updateStations(stationsById) {
        let idsOfStationsInListView = this.stationIds;
        let idsToRemove = [];
        
        for (let i = 0; i < idsOfStationsInListView.length; i++) {
            let id = idsOfStationsInListView[i];
            
            if (id in stationsById) {
                this.stations[id].update(stationsById[id]);
            } else {
                console.info(`station id ${id} no longer in data result. Need to remove.`);
                idsToRemove.push(id);
            }
        }
        
        for (let i = 0; i < idsToRemove.length; i++) {
            let idToRemove = idsToRemove[i];
            this.removeStation(idToRemove, true);
        }
        
        let newStationsInData = Object.keys(stationsById).filter(id => !(id in this.stations)).map(id => stationsById[id]);
        
        for (let i = 0; i < newStationsInData.length; i++) {
            let stationToAdd = newStationsInData[i];
            this.addStation(stationToAdd, true);
        }
        
        this.updateView();
    }
    
    addListItemMouseenterListener(callback) {
        if (this._onListItemMouseenterListeners.indexOf(callback) === -1) {
            this._onListItemMouseenterListeners.push(callback);
        }
    }
    
    invokeListItemMouseenterListeners(listItem, event) {
        for (let i = 0, x = this._onListItemMouseenterListeners.length; i < x; i++) {
            this._onListItemMouseenterListeners[i](listItem, event);
        }
    }
    
    addListItemMouseleaveListener(callback) {
        if (this._onListItemMouseleaveListeners.indexOf(callback) === -1) {
            this._onListItemMouseleaveListeners.push(callback);
        }
    }
    
    invokeListItemMouseleaveListeners(listItem, event) {
        for (let i = 0, x = this._onListItemMouseleaveListeners.length; i < x; i++) {
            this._onListItemMouseleaveListeners[i](listItem, event);
        }
    }
}

export default StationListView;